<template>
  <div class="contacts-section" id="contacts">
    <div class="section-header text-center mb-12">
      <h2 class="section-title">The team</h2>
      <p class="section-subtitle max-width mx-auto">
        Feel free to contact us if you have feedback or suggestions for any project, or if you are experiencing technical issues.
Please refer to the contact data below.
      </p>
    </div>

    <div class="contact-grid">
      <div
        v-for="contact in Object.values(contact_data)"
        :key="contact.name"
        class="contact-card"
      >
        <div class="avatar-container">
          <div class="avatar" :style="{ backgroundColor: getAvatarColor(contact.shorthand) }">
            <span class="avatar-text">{{ contact.shorthand }}</span>
          </div>
        </div>

        <div class="contact-info">
          <h3 class="contact-name">{{ contact.name }}</h3>
          <p class="contact-role">{{ contact.role }}</p>

          <div v-if="contact.email" class="contact-email">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="email-icon">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <a
              :href="`mailto:${contact.email}`"
              class="email-link"
            >
              {{ contact.email }}
            </a>
          </div>

          <div class="contact-projects">
            <h4 class="projects-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="project-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              Projects
            </h4>
            <div class="projects-list">
              <span
                v-for="project in contact.projects"
                :key="project"
                class="project-tag"
                :style="{ backgroundColor: getProjectColor(project) + '20', color: getProjectColor(project) }"
              >
                {{ project }}
              </span>
            </div>
          </div>

          <div v-if="contact.email" class="contact-actions">
            <a
              :href="`mailto:${contact.email}`"
              class="contact-button"
            >
              <span>Contact</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      contact_data: {
        "JD": {
          name: "Jürgen Dönitz",
          shorthand: "JD",
          projects: ['drugon', 'seqcat', 'onkopus', 'cnvdb', 'mtbreport'],
          role: "Project Lead",
          email: "juergen.doenitz@bioinf.med.uni-goettingen.de"
        },
        "NS": {
          name: "Nadine Sina Kurz",
          shorthand: "NSK",
          projects: ['onkopus', 'adagenes', 'mtb-report', 'cnvdb', 'seqcat'],
          role: "Researcher",
          email: "nadine.kurz@bioinf.med.uni-goettingen.de"
        },
        "KK": {
          name: "Kevin Kornrumpf",
          shorthand: "KK",
          projects: ['drugon', 'seqcat', 'onkopus', 'cnvdb'],
          role: "Researcher",
          email: ""
        },
        "KD": {
          name: "Klara Drofenik",
          shorthand: "KD",
          projects: ['onkopus', 'seqcat'],
          role: "Contributor",
          email: ""
        },
        "TT": {
          name: "Tim Tucholski",
          shorthand: "TT",
          projects: ['onkopus'],
          role: "Contributor",
          email: ""
        }
      }
    }
  },
  methods: {
    getAvatarColor(shorthand) {
      const colors = ['#dbeafe', '#ede9fe', '#fef3c7', '#dcfce7', '#ffedd5'];
      return colors[shorthand.charCodeAt(0) % colors.length];
    },
    getProjectColor(project) {
      const colors = {
        'drugon': '#2563eb',
        'seqcat': '#7c3aed',
        'onkopus': '#d97706',
        'cnvdb': '#8b5cf6',
        'mtbreport': '#059669'
      };
      return colors[project] || '#64748b';
    }
  }
}
</script>

<style scoped>
:root {
  --primary: #2563eb;
  --primary-dark: #1d4ed8;
  --secondary: #7c3aed;
  --light: #f8fafc;
  --dark: #0f172a;
  --gray: #64748b;
  --light-gray: #e2e8f0;
  --transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.contacts-section {
  padding: 6rem 5%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  background-color: var(--light);
  position: relative;
  overflow: hidden;
}

.contacts-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
}

.section-header {
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--dark);
  margin-bottom: 1.5rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--gray);
  line-height: 1.7;
}

.max-width {
  max-width: 700px;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2.5rem;
}

.contact-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: var(--transition);
  overflow: hidden;
  position: relative;
  border: 1px solid var(--light-gray);
}

.contact-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.avatar-container {
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.avatar-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--dark);
}

.contact-info {
  padding: 1.5rem;
}

.contact-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--dark);
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.contact-role {
  font-size: 0.95rem;
  color: var(--gray);
  margin-bottom: 1.25rem;
}

.contact-email {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  color: var(--gray);
}

.email-icon {
  margin-right: 0.75rem;
  color: var(--gray);
  min-width: 16px;
}

.email-link {
  color: var(--primary);
  text-decoration: none;
  transition: var(--transition);
  font-size: 0.95rem;
}

.email-link:hover {
  text-decoration: underline;
  color: var(--primary-dark);
}

.contact-projects {
  margin-bottom: 1.5rem;
}

.projects-title {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--gray);
  margin-bottom: 0.75rem;
  gap: 0.5rem;
}

.project-icon {
  color: var(--gray);
  min-width: 16px;
}

.projects-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.project-tag {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}

.contact-actions {
  display: flex;
  justify-content: flex-end;
}

.contact-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: linear-gradient(to right, #dbeafe, #ede9fe);
  color: var(--primary) !important;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: var(--transition);
  border: 1px solid rgba(37, 99, 235, 0.2);
}

.contact-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.2);
}

.contact-button svg {
  transition: var(--transition);
  stroke: var(--primary);
}

.contact-button:hover svg {
  transform: translateX(3px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .contact-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .contacts-section {
    padding: 4rem 5%;
  }

  .section-title {
    font-size: 1.8rem;
  }
}

@media (max-width: 480px) {
  .contacts-section {
    padding: 3rem 4%;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .contact-card {
    max-width: 100%;
  }
}
</style>